import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { updateAllStoreLookups } from "@/components/Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import {
  type StoryboardDelta,
  UpdateStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import { getUniqueName } from "@/utils/fileNameUtils";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import type { Storyboard } from "@/utils/types";
import { useActiveCanvas, useCanvasId } from "@/utils/utils";

export const useDuplicateStoryboard = () => {
  const canvasId = useCanvasId();
  const canvasIdRef = useRef(canvasId);
  useEffect(() => {
    canvasIdRef.current = canvasId;
  }, [canvasId]);

  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const duplicateStoryboard = async (
    storyboardToDuplicate: Storyboard,
    { overrides = {} }: { overrides?: Partial<Storyboard> } = {},
  ) => {
    if (!canvasRef.current) {
      console.error("Canvas not available");
      return;
    }

    const htmlFromSource =
      useIframePoolStore.getState().htmlForStoryboard[storyboardToDuplicate.id];
    const navTreeFromSource =
      useCanvasStore.getState().storyboardToNavTree[storyboardToDuplicate.id];

    const {
      html: processedHtmlForInstantUpdate,
      navTree: processedNavTreeForInstantUpdate,
    } = processInstantUpdateData({
      storyboardId: storyboardToDuplicate.id,
      canvasId,
      instantUpdateData: {
        html: htmlFromSource,
        navTree: navTreeFromSource,
      },
      wrapInShell: true,
    });

    const width = storyboardToDuplicate.width;
    const height = storyboardToDuplicate.height;

    const insertX = storyboardToDuplicate.x + width + 20;
    const insertY = storyboardToDuplicate.y;

    const maxZIndex = canvasRef.current?.storyboards
      ?.map((storyboard: any) => storyboard.zIndex)
      ?.reduce((a: any, b: any) => Math.max(a, b), 0);

    track("STORYBOARD_CREATE", {
      type: storyboardToDuplicate.type,
      duplicate: true,
    });

    // Instant updates
    const newStoryboardId = uuidv4().toString();
    if (processedHtmlForInstantUpdate) {
      useIframePoolStore
        .getState()
        .setHtmlForStoryboard(newStoryboardId, processedHtmlForInstantUpdate);
    }

    // If used default, don't update the nav tree to show a loading state
    if (processedNavTreeForInstantUpdate) {
      updateAllStoreLookups(
        newStoryboardId,
        { navTree: processedNavTreeForInstantUpdate },
        true,
      );
    }

    if (
      storyboardToDuplicate.type === STORYBOARD_TYPES.APPLICATION ||
      storyboardToDuplicate.saved
    ) {
      // Get page info for updating page.json
      const pagesStore = usePagesStore.getState();
      const selectedPageId = pagesStore.selectedPageId[canvasRef.current.id];
      const pageConfigPath = selectedPageId
        ? pagesStore.getPageSourcePath(canvasRef.current.id, selectedPageId)
        : undefined;

      // Just duplicate the storyboard without modifying code
      const newStoryboard = {
        ...storyboardToDuplicate,
        id: newStoryboardId,
        width,
        height,
        x: insertX,
        y: insertY,
        zIndex: maxZIndex + 10,
        ...overrides,
      };

      const storyboardDeltas: StoryboardDelta[] = [
        {
          changeType: "upsert",
          storyboardId: newStoryboardId,
          pageId: storyboardToDuplicate.pageId || "",
          pageConfigPath,
          storyboardUpdateDict: newStoryboard,
        },
      ];

      await updateCanvasStoryboards(
        canvasRef.current.id,
        storyboardDeltas,
        undefined,
        true,
      );
      return;
    }

    try {
      // Get all existing names to ensure uniqueness
      const storyboardAndComponentNames = new Set<string>();

      canvasRef.current?.storyboards
        .filter(
          (storyboard: any) => storyboard.type !== STORYBOARD_TYPES.APPLICATION,
        )
        .filter((storyboard: any) => storyboard.deleted !== true)
        .forEach((storyboard: any) => {
          storyboardAndComponentNames.add(storyboard.name);
        });

      Object.values(
        useCodebaseStore.getState().treeElementLookup || {},
      ).forEach((treeElement: any) => {
        if (treeElement.type === "component") {
          storyboardAndComponentNames.add(treeElement.name);
        }
      });

      // Preserve source storyboard name with collision handling
      const baseName = storyboardToDuplicate.name || "Storyboard1";
      const storyboardName = getUniqueName(
        baseName,
        storyboardAndComponentNames,
      );

      const newStoryboard = {
        name: storyboardName,
        id: newStoryboardId,
        type: STORYBOARD_TYPES.COMPONENT,
        width,
        height,
        x: insertX,
        y: insertY,
        zIndex: maxZIndex + 10,
        created: true,
        ...overrides,
      };

      // Get page info for updating page.json
      const pagesStore = usePagesStore.getState();
      const selectedPageId = pagesStore.selectedPageId[canvasRef.current.id];
      const pageConfigPath = selectedPageId
        ? pagesStore.getPageSourcePath(canvasRef.current.id, selectedPageId)
        : undefined;

      const change = new UpdateStoryboardsChange({
        deltas: [
          {
            changeType: "upsert",
            storyboardId: newStoryboard.id,
            pageId: storyboardToDuplicate.pageId || "",
            pageConfigPath,
            storyboardUpdateDict: newStoryboard,
          },
        ],
        duplicateStoryboardFields: {
          newStoryboardName: storyboardName,
          sourceStoryboardId: storyboardToDuplicate.id,
        },
        canvasId: canvasRef.current.id,
      });

      await useDesignCommandManager.getState().executeCommandAsPromise(change);
    } catch (error) {
      toast.error("Error duplicating storyboard");
      console.error("Error duplicating storyboard: ", error);
    }
  };

  return duplicateStoryboard;
};
